/* ------------------------------------------------------------------
 * Content-shaped loading placeholders.
 *
 * Every page used to wait behind PageLoader — a centred brand spinner in an
 * empty panel. It says "something is happening" and nothing about what, the
 * layout is empty until the data lands, and the page then jumps as the real
 * rows push it open. A skeleton that mirrors the content reserves that space
 * and reads as the page arriving rather than the page being absent, which is
 * what the Spotlight catalogue already did (SpotlightGridSkeleton) and what
 * the other six templates did not.
 *
 * TWO RULES when adding one:
 *
 *   1. Mirror the real markup it stands in for — same wrapper, same padding,
 *      same block heights. A placeholder of the wrong height is worse than a
 *      spinner: it promises a layout and then moves it.
 *   2. Neutrals through the ramp (`bg-gray-100`), never a literal grey hex, or
 *      the skeleton stays light while the rest of the app goes dark. This is
 *      why these use `animate-pulse` on a ramp colour rather than the shimmer
 *      gradient in PageLoader, whose stops are hardcoded.
 *
 * `count` should track the real page size, not a round number — six cards
 * under a heading, then two arriving, reads as a page that shrank.
 * ------------------------------------------------------------------ */

/** One placeholder block. `className` carries the size and any shape override. */
export function Skel({ className = "" }) {
  return <div className={`animate-pulse rounded bg-gray-100 ${className}`} aria-hidden="true" />;
}

/* ── course / catalogue cards ─────────────────────────────────────── */

/**
 * The shape of a catalogue card: an inset `p-2 pb-0` cover frame at 16:9 — the
 * same aspect ratio the real card's cover uses, so the page does not jump when
 * the cards land — then a p-4 body with a title, two description lines and a
 * meta row.
 */
export function CardSkeleton() {
  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-surface shadow-sm">
      <div className="p-2 pb-0">
        <Skel className="aspect-video w-full !rounded-lg" />
      </div>
      <div className="p-4">
        <Skel className="h-4 w-2/3" />
        <Skel className="mt-2.5 h-3 w-full" />
        <Skel className="mt-1.5 h-3 w-4/5" />
        <Skel className="mt-4 h-2.5 w-1/2 !rounded-full" />
      </div>
    </div>
  );
}

/** The Campus catalogue lays its cards out horizontally — thumbnail left, content right. */
export function RowCardSkeleton() {
  return (
    <div className="flex gap-4 overflow-hidden rounded-xl border border-gray-200 bg-surface p-3 shadow-sm">
      <Skel className="h-24 w-40 shrink-0 !rounded-lg sm:h-28 sm:w-48" />
      <div className="min-w-0 flex-1 py-1">
        <Skel className="h-4 w-1/3" />
        <Skel className="mt-2.5 h-3 w-4/5" />
        <Skel className="mt-1.5 h-3 w-2/3" />
        <Skel className="mt-4 h-2 w-full !rounded-full" />
      </div>
    </div>
  );
}

/** Columns used by the non-Spotlight course grid (CourseViews `courseGrid`). */
export const CARD_GRID = "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5";

/**
 * A catalogue of placeholder cards.
 *
 * `grid` must be the SAME class string the real grid uses — the templates lay
 * their catalogues out differently (two-up, three-up, one big column, a
 * horizontal stack) and a skeleton in the wrong columns re-flows the page the
 * moment the cards land, which is the thing it exists to prevent.
 */
export function CardGridSkeleton({ count = 6, grid = CARD_GRID, row = false }) {
  const Card = row ? RowCardSkeleton : CardSkeleton;

  return (
    <div className={grid} aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} />
      ))}
    </div>
  );
}

/* ── people ───────────────────────────────────────────────────────── */

/** A member row: avatar, name + meta line, points chip. Mirrors the Members list. */
export function PersonRowSkeleton() {
  return (
    <li className="flex w-full items-center gap-3.5 rounded-xl border border-gray-100 bg-surface p-3 shadow-sm">
      <Skel className="h-11 w-11 shrink-0 !rounded-full" />
      <div className="min-w-0 flex-1">
        <Skel className="h-3.5 w-1/3" />
        <Skel className="mt-2 h-3 w-1/4" />
      </div>
      <Skel className="h-7 w-16 shrink-0 !rounded-lg" />
    </li>
  );
}

export function PersonListSkeleton({ count = 6 }) {
  return (
    <ul className="space-y-2.5 bg-gray-50/40 p-4" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <PersonRowSkeleton key={i} />
      ))}
    </ul>
  );
}

/**
 * Leaderboard: the three-column podium, then the ranked rows.
 *
 * The podium is the tallest thing on the page and it is what pushes the rows
 * down when it lands, so it is worth standing in for — a rows-only skeleton
 * would be shoved 200px down the moment the data arrives.
 */
export function LeaderboardSkeleton({ count = 7 }) {
  return (
    <div aria-hidden="true">
      <div className="flex items-end justify-center gap-4 px-4 pb-2 pt-6 sm:gap-8">
        {[88, 112, 72].map((h, i) => (
          <div key={i} className="flex w-20 flex-col items-center sm:w-24">
            <Skel className={`${i === 1 ? "h-16 w-16" : "h-12 w-12"} !rounded-full`} />
            <Skel className="mt-2 h-3 w-14" />
            <Skel className="mt-1.5 h-2.5 w-10" />
            <div className="mt-3 w-full" style={{ height: h }}>
              <Skel className="h-full w-full !rounded-t-xl" />
            </div>
          </div>
        ))}
      </div>
      <ul className="space-y-3 px-4 pb-5 pt-4 sm:px-5">
        {Array.from({ length: count }).map((_, i) => (
          <li key={i} className="flex items-center gap-3 rounded-full border border-gray-100 py-3 pl-4 pr-5">
            <Skel className="h-9 w-9 shrink-0 !rounded-full" />
            <div className="min-w-0 flex-1">
              <Skel className="h-3.5 w-1/4" />
            </div>
            <Skel className="h-6 w-14 shrink-0 !rounded-full" />
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── events ───────────────────────────────────────────────────────── */

/** An event row: date block on the left, title + meta, a join button. */
export function EventListSkeleton({ count = 3 }) {
  return (
    <div className="space-y-6 p-4 sm:p-5" aria-hidden="true">
      <div>
        <Skel className="mb-3 h-3.5 w-24" />
        <div className="space-y-3">
          {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 rounded-xl border border-gray-200 bg-surface p-4 shadow-sm">
              <Skel className="h-14 w-14 shrink-0 !rounded-xl" />
              <div className="min-w-0 flex-1">
                <Skel className="h-4 w-2/5" />
                <Skel className="mt-2.5 h-3 w-3/5" />
                <Skel className="mt-2 h-2.5 w-1/4" />
              </div>
              <Skel className="hidden h-9 w-24 shrink-0 !rounded-lg sm:block" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── rewards ──────────────────────────────────────────────────────── */

/** Rewards sit two-up on mobile and three-up on desktop — tighter than a course card. */
export function RewardGridSkeleton({ count = 6 }) {
  return (
    <div className="grid grid-cols-2 gap-3 p-4 sm:gap-4 sm:p-5 lg:grid-cols-3" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="overflow-hidden rounded-xl border border-gray-200 bg-surface shadow-sm">
          <Skel className="h-28 w-full !rounded-none" />
          <div className="p-3.5">
            <Skel className="h-3.5 w-3/4" />
            <Skel className="mt-2 h-3 w-1/2" />
            <Skel className="mt-3.5 h-7 w-full !rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── map ──────────────────────────────────────────────────────────── */

/** The map canvas itself, at the height the real map takes so nothing jumps. */
export function MapSkeleton() {
  return (
    <div className="relative h-[560px] w-full overflow-hidden rounded-xl bg-gray-100 max-md:h-[400px]" aria-hidden="true">
      <div className="absolute inset-0 animate-pulse bg-gray-100" />
      {/* A few pins, so the block reads as a map rather than a dead panel. */}
      {[
        ["28%", "22%"], ["58%", "38%"], ["44%", "62%"], ["72%", "18%"], ["18%", "68%"],
      ].map(([top, left], i) => (
        <span
          key={i}
          className="absolute h-8 w-8 animate-pulse rounded-full bg-gray-200 ring-4 ring-surface"
          style={{ top, left }}
        />
      ))}
    </div>
  );
}

/* ── lesson ───────────────────────────────────────────────────────── */

/**
 * A lesson: the course header (cover · title · meta · progress panel), then the
 * curriculum rail and the player block beside it.
 *
 * The header and the back link are part of the shape on purpose. Leaving them
 * out meant every refresh of a course page drew the rail and the player with
 * nothing above them, so for the length of the api/lessonlist round trip the
 * banner looked absent rather than pending — reported as "the top section is
 * sometimes missing after a refresh", and invisible on a fast connection.
 *
 * The columns mirror the live Classic layout: rail LEFT at 4 of 12, player
 * right at 8. This stood the other way round, which flipped the whole page
 * sideways the moment the data landed.
 */
export function LessonSkeleton({ template = "classic" } = {}) {
  // The player has two layouts (CourseViews CoursePlayer): FlexiFunnels, and
  // Classic for every other template. The skeleton has to be the one that is
  // about to replace it, or a refresh shows one page and lands on another.
  if (template === "flexifunnels") return <FlexiLessonSkeleton />;
  return (
    <div aria-hidden="true">
      {/* "Back to courses" */}
      <Skel className="h-5 w-32" />

      {/* Course header: cover · info · progress panel */}
      <div className="mt-4 flex flex-col overflow-hidden rounded-3xl bg-surface shadow-[0_10px_40px_-16px_rgba(15,23,42,0.28)] ring-1 ring-gray-900/[0.06] lg:min-h-[6.5rem] lg:flex-row">
        <Skel className="h-40 w-full shrink-0 !rounded-none lg:h-auto lg:w-64 xl:w-72" />
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-2 px-6 py-4">
          <Skel className="h-6 w-2/5" />
          <Skel className="h-3 w-3/5" />
          {/* the meta pills: modules · lessons · access · rating */}
          <div className="mt-0.5 flex flex-wrap items-center gap-2">
            <Skel className="h-6 w-24 !rounded-full" />
            <Skel className="h-6 w-24 !rounded-full" />
            <Skel className="h-6 w-16 !rounded-full" />
            <Skel className="h-6 w-14 !rounded-full" />
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-center justify-center gap-2.5 border-t border-gray-100 px-7 py-4 lg:w-64 lg:border-l lg:border-t-0">
          <div className="flex items-center gap-3">
            <Skel className="h-[54px] w-[54px] !rounded-full" />
            <div>
              <Skel className="h-5 w-12" />
              <Skel className="mt-1.5 h-2.5 w-20" />
            </div>
          </div>
          <Skel className="h-2.5 w-28" />
          <Skel className="h-2 w-full max-w-[12rem] !rounded-full" />
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-12">
        {/* curriculum rail */}
        <div className="space-y-3 rounded-2xl bg-surface p-4 shadow-[0_6px_24px_-12px_rgba(15,23,42,0.18)] ring-1 ring-gray-900/[0.06] lg:col-span-4">
          <Skel className="h-4 w-1/3" />
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 rounded-xl border border-gray-100 p-3">
              <Skel className="h-8 w-8 shrink-0 !rounded-lg" />
              <div className="min-w-0 flex-1">
                <Skel className="h-3 w-2/3" />
                <Skel className="mt-2 h-2.5 w-1/3" />
              </div>
            </div>
          ))}
        </div>

        {/* player + description */}
        <div className="space-y-4 lg:col-span-8">
          <Skel className="aspect-video w-full !rounded-2xl" />
          <Skel className="h-6 w-2/5" />
          <div className="space-y-2.5">
            <Skel className="h-3 w-full" />
            <Skel className="h-3 w-11/12" />
            <Skel className="h-3 w-4/5" />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The FlexiFunnels player while its lessons load. Mirrors the live layout in
 * CourseViews (`template === "flexifunnels"`): a breadcrumb, then the player
 * card LEFT at 8 of 12 — video, title row with the action button, the
 * Module · Lesson line, the About / Discussions / Notes / Resources tabs and
 * the lesson text — and the modules rail RIGHT at 4 of 12.
 */
function FlexiLessonSkeleton() {
  return (
    <div aria-hidden="true">
      {/* breadcrumb: Home / course name */}
      <div className="flex items-center gap-2">
        <Skel className="h-4 w-10" />
        <Skel className="h-4 w-48" />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-5 lg:grid-cols-12">
        {/* player card (left) */}
        <div className="overflow-hidden rounded-2xl bg-surface shadow-[0_10px_40px_-16px_rgba(15,23,42,0.18)] ring-1 ring-gray-900/[0.06] lg:col-span-8">
          <Skel className="aspect-video w-full !rounded-none" />
          <div className="p-5 sm:p-7">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 sm:flex-1">
                <Skel className="h-7 w-3/5" />
                <Skel className="mt-2.5 h-3 w-4/5" />
              </div>
              <Skel className="h-8 w-40 shrink-0 !rounded-full" />
            </div>
            {/* tabs */}
            <div className="mt-6 flex gap-6 border-b border-gray-100 pb-3">
              <Skel className="h-4 w-24" />
              <Skel className="h-4 w-20" />
              <Skel className="h-4 w-12" />
              <Skel className="h-4 w-20" />
            </div>
            <div className="mt-5 space-y-2.5">
              <Skel className="h-3 w-full" />
              <Skel className="h-3 w-11/12" />
              <Skel className="h-3 w-4/5" />
              <Skel className="h-3 w-full" />
              <Skel className="h-3 w-3/5" />
            </div>
          </div>
        </div>

        {/* modules rail (right) */}
        <div className="lg:col-span-4">
          <div className="overflow-hidden rounded-2xl bg-surface shadow-[0_6px_24px_-12px_rgba(15,23,42,0.18)] ring-1 ring-gray-900/[0.06]">
            {/* "Total N Modules" */}
            <div className="border-b border-gray-100 px-4 py-3">
              <Skel className="h-4 w-28" />
            </div>
            {/* lessons done · track · percent */}
            <div className="flex items-center gap-3 border-b border-gray-100 px-4 py-2.5">
              <Skel className="h-3 w-16" />
              <Skel className="h-2 min-w-0 flex-1 !rounded-full" />
              <Skel className="h-3 w-8" />
            </div>
            <div className="space-y-2 p-2.5">
              {/* module header */}
              <div className="flex items-start gap-3 rounded-xl border border-gray-100 p-3">
                <Skel className="h-11 w-11 shrink-0 !rounded-xl" />
                <div className="min-w-0 flex-1">
                  <Skel className="h-3.5 w-3/4" />
                  <Skel className="mt-2 h-2.5 w-full" />
                  <Skel className="mt-1.5 h-2.5 w-2/3" />
                  <Skel className="mt-3 h-1.5 w-full !rounded-full" />
                </div>
              </div>
              {/* lesson rows */}
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-2.5">
                  <Skel className="h-6 w-6 shrink-0 !rounded-md" />
                  <Skel className="h-3 w-2/3" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
